/**
 * Money is always an integer in the currency's minor unit, never a float.
 *
 * TWD and JPY are zero-decimal currencies: `minor` for TWD *is* whole dollars.
 * Nothing in this codebase may assume a x100 conversion -- the sample bank file
 * contains both `TWD 16,708` and `JPY 80,940.00`.
 */

export const CURRENCY_EXPONENT: Record<string, number> = {
  TWD: 0,
  JPY: 0,
  KRW: 0,
  USD: 2,
  EUR: 2,
  GBP: 2,
  CNY: 2,
  HKD: 2,
}

export type CurrencyCode = string

export function exponentOf(currency: CurrencyCode): number {
  const e = CURRENCY_EXPONENT[currency.toUpperCase()]
  if (e === undefined) {
    throw new Error(`Unknown currency: ${currency}`)
  }
  return e
}

/** Parse a decimal string in major units into integer minor units. */
export function toMinor(major: string | number, currency: CurrencyCode): number {
  const exp = exponentOf(currency)
  const text = typeof major === 'number' ? major.toString() : major.trim()
  if (!/^-?\d+(\.\d+)?$/.test(text)) {
    throw new Error(`Not a numeric amount: ${JSON.stringify(major)}`)
  }
  const negative = text.startsWith('-')
  const [intPart = '0', fracPart = ''] = text.replace(/^-/, '').split('.')
  const fracPadded = (fracPart + '0'.repeat(exp)).slice(0, exp)
  // Any digits beyond the currency's precision are a data error, not something
  // to silently round: TWD 101.5 does not exist.
  const extra = fracPart.slice(exp)
  if (extra.replace(/0+$/, '').length > 0) {
    throw new Error(`Amount ${text} has more precision than ${currency} allows`)
  }
  const minor = Number(intPart) * 10 ** exp + (exp > 0 ? Number(fracPadded || '0') : 0)
  return negative ? -minor : minor
}

/** Render integer minor units for display, without a currency symbol. */
export function formatMinor(minor: number, currency: CurrencyCode): string {
  const exp = exponentOf(currency)
  const negative = minor < 0
  const abs = Math.abs(minor)
  if (exp === 0) {
    return (negative ? '-' : '') + abs.toLocaleString('en-US')
  }
  const divisor = 10 ** exp
  const intPart = Math.floor(abs / divisor)
  const fracPart = (abs % divisor).toString().padStart(exp, '0')
  return `${negative ? '-' : ''}${intPart.toLocaleString('en-US')}.${fracPart}`
}

export function assertInteger(value: number, label: string): void {
  if (!Number.isInteger(value)) {
    throw new Error(`${label} must be an integer minor amount, got ${value}`)
  }
}
